/* eslint-disable @nx/enforce-module-boundaries */
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { appRoutes } from './app.routes';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideHttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideTransloco, TranslocoService } from '@ngneat/transloco';
import { FORMLY_CONFIG, FormlyModule, FormlyFieldConfig } from '@ngx-formly/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { MatStepperModule } from '@angular/material/stepper';
import { FormlyPresetModule } from '@ngx-formly/core/preset';
import { provideLottieOptions } from 'ngx-lottie';
import { providePrimeNG } from 'primeng/config';
import Material from '@primeng/themes/material';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { provideAuth, initializeAuth } from '@angular/fire/auth';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { includeBearerTokenInterceptor } from 'keycloak-angular';

// Zopup library imports
import { provideZopAuth, provideZopKeycloakAuth, provideZopAuthConfig } from '@zopup/zop-auth';
import { provideZopApis } from '@zopup/zop-apis';
import { provideZopCore } from '@zopup/zop-core';
import { provideZopUxCore, zopLoadingInterceptor } from '@zopup/zop-ux-core';
import { provideZopUxLayoutCore } from '@zopup/zop-ux-layout';
import {
    COUNTRY_LIST, FormlyFieldStepper, PanelWrapperComponent, SALUTATION_LIST, ZopFileUploadComponent, ZopButtonField, registerCountryPreset,
    registerEMailAddressPreset,
    registerSalutationPreset,
    yearDifferenceValidator,
    toFromDateValidator,
    TimepickerComponent,
    SelectTagsComponent,
    ZopAutoCompleteComponent,
    invalidEffectiveDateRangeValidator,
    globalErrorInterceptor,
} from '@zopup/zop-shared';
import { Salutation } from '@zopup/zop-types';

// Internal project imports
import { TranslocoHttpLoader } from './providers/transloco/transloco.http-loader';
import { provideIcons } from './providers/icons/icons.provider';
import { mockApiServices } from './mock-api/mock-service.const';
import { environment } from '../environment/environment';
import { ADMIN_APP_AUTH_CONFIG } from './config/zop-auth.config';
import { countries } from './mock-api/countries/data';

const fbApp = () => initializeApp(environment.firebase);
const authApp = () => initializeAuth(fbApp());
const fbDb = () => getFirestore(fbApp());
const firebaseProviders = [
    provideFirebaseApp(fbApp),
    provideAuth(authApp),
    provideFirestore(fbDb),
];

export const types: any[] = [
    { name: 'autocomplete', component: ZopAutoCompleteComponent, wrappers: [] },
    { name: 'stepper', component: FormlyFieldStepper, wrappers: [] },
    { name: 'file', component: ZopFileUploadComponent, wrappers: [] },
    {
        name: 'button',
        component: ZopButtonField,
        defaultOptions: {
          props: {
            btnType: 'default',
            type: 'button',
          },
        },
    },
    {
        name: 'timepicker',
        component: TimepickerComponent,
    },
    {
        name: 'select-tags',
        component: SelectTagsComponent
    }
];

export const wrappers: any[] = [
    { name: 'panel', component: PanelWrapperComponent },
];

export function minLengthValidationMessage(
    error: any,
    field: FormlyFieldConfig
) {
    return `Should have atleast ${field.props.minLength} characters`;
}

export function maxLengthValidationMessage(
    error: any,
    field: FormlyFieldConfig
) {
    return `This value should be less than ${field.props.maxLength} characters`;
}

export function minValidationMessage(error: any, field: FormlyFieldConfig) {
    return `This value should be more than ${field.props.min}`;
}

export function maxValidationMessage(error: any, field: FormlyFieldConfig) {
    return `This value should be less than ${field.props.max}`;
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Angular fire base provider
        importProvidersFrom(
            // [
            // AngularFireModule.initializeApp(environment.firebase),
            FormlyModule.forRoot({
                types: types,
                wrappers: wrappers,
                validators: [
                    { name: 'toFromDateValidator', validation: toFromDateValidator },
                    { name: 'yearDifferenceValidator', validation: yearDifferenceValidator },
                    {
                        name: 'invalidEffectiveDateRange',
                        validation: invalidEffectiveDateRangeValidator,
                    },
                ],
                validationMessages: [
                    { name: 'required', message: 'This field is required' },
                    { name: 'minLength', message: minLengthValidationMessage },
                    { name: 'maxLength', message: maxLengthValidationMessage },
                    { name: 'min', message: minValidationMessage },
                    { name: 'max', message: maxValidationMessage },
                    {
                        name: 'toFromDateValidator',
                        message: 'To Date must be later than From Date',
                    },
                    {
                        name: 'invalidEffectiveDateRange',
                        message: 'Effective From date must be earlier than Effective To date.',
                    },
                    {
                        name: 'yearDifferenceValidator',
                        message:
                            'Difference between To Date and From Date should be 1 year',
                    },
                ],
            }),
            FormsModule,
            ReactiveFormsModule,
            FormlyMaterialModule,
            MatStepperModule,
            FormlyPresetModule,
        // ]
        ),
        ...firebaseProviders,
        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },
        {
            provide: SALUTATION_LIST,
            useValue: Object.keys(Salutation),
        },
        {
            provide: FORMLY_CONFIG,
            useFactory: registerSalutationPreset,
            deps: [SALUTATION_LIST],
            multi: true,
        },
        {
            provide: COUNTRY_LIST,
            useValue: countries,
        },
        {
            provide: FORMLY_CONFIG,
            useFactory: registerCountryPreset,
            deps: [COUNTRY_LIST],
            multi: true,
        },
        {
            provide: FORMLY_CONFIG,
            useFactory: registerEMailAddressPreset,
            multi: true,
        },
        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'tr',
                        label: 'Turkish',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: true,
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
        const initializerFn = (() => {
                const translocoService = inject(TranslocoService);
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);

                return () => firstValueFrom(translocoService.load(defaultLang));
            })();
        return initializerFn();
      }),
        // Zop Providers
        provideZopAuth(ADMIN_APP_AUTH_CONFIG),
        provideZopApis(environment),

        provideIcons(),
        provideZopCore({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            httpInterceptors: [
                globalErrorInterceptor,         // Global HTTP error handling interceptor
                zopLoadingInterceptor,          // Loading indicator interceptor
                includeBearerTokenInterceptor,   // Keycloak Bearer token interceptor
            ],
        }),
        provideZopUxCore(),
        provideZopUxLayoutCore({
            layout: 'thin',
            scheme: 'light',
            screens: {
                sm: '600px',
                md: '960px',
                lg: '1280px',
                xl: '1440px',
            },
            theme: 'theme-default',
            themes: [
                {
                    id: 'theme-default',
                    name: 'Default',
                },
                {
                    id: 'theme-brand',
                    name: 'Brand',
                },
                {
                    id: 'theme-teal',
                    name: 'Teal',
                },
                {
                    id: 'theme-rose',
                    name: 'Rose',
                },
                {
                    id: 'theme-purple',
                    name: 'Purple',
                },
                {
                    id: 'theme-amber',
                    name: 'Amber',
                },
            ],
            navigationUrl: 'api/common/navigation',
            isProduction: false,
            hideFooter: true,
            hideHeader: true,
            copyWriteText: 'eValuz',
        }),
        provideLottieOptions({
            player: () => import('lottie-web'),
        }),
        // Prime Ng
        providePrimeNG({
            theme: {
                preset: Material,
                options: {
                    cssLayer: {
                        name: 'primeng',
                        order: 'tailwind-base, primeng, tailwind-utilities',
                    },
                    darkModeSelector: false, // Class to trigger dark mode
                },
            },
        }),

        // Keycloak (modern setup)
        ...provideZopKeycloakAuth({
            config: environment.keycloak, // { url, realm, clientId }
            excludeHttpClient: true, // Let provideZopCore handle HTTP client with interceptors
        }),
    ],
};
